/**
 * Askalabel storefront search — match the product NAME and DESCRIPTION, and
 * nothing else.
 *
 * WHY (Andre, 2026-10-01: "Maybe title and description then. I think the
 * anonym is the one that messed up the search"). Searching a design name
 * returned the catalogue: `chandana` found 122 products, 7 of which carry the
 * word. Measured the same day, suggestions six to ten were Black Kanda, Light
 * Grey Kandara, Sand Kanda, Black Kandara and Deep Cranberry Kalinda — and
 * that word appears in NOTHING on those products: not the title, not the
 * description, not the tags, not the product type, not a SKU. Shopify was
 * answering from its own synonym/fuzzy layer. Same for `hitam`, where all ten
 * suggestions were Black items with "hitam" nowhere in them.
 *
 * WHAT THIS DOES. Shopify honours field qualifiers inside `q`, and a qualified
 * term turns that layer off. So each word typed is sent as
 *
 *     title:<word> OR body:<word> OR variants.sku:<word>
 *
 * `body:` is the description. `description:` is NOT a qualifier — it silently
 * degrades to an ordinary search, which is why it reported 490 results for
 * `description:linen` against 33 for `body:linen`. Groups are ANDed, measured
 * on the live shop: chandana 122 -> 7, dress 291 -> 47, basic dress 185 -> 19,
 * black chandana 104 -> 1 (Black Chandana), and A390113 still lands on Ice Grey
 * Chandana through the SKU branch.
 *
 * TWO THINGS THE QUALIFIER COSTS. Both are handled here, and both are the
 * reason this file is not a one-liner:
 *
 * 1. NO PARTIAL WORDS. `title:chandan` is 0 where bare `chandan` is 125, and a
 *    wildcard does not help (`title:chandan*` -> 0): a qualified term matches
 *    whole words only. So when the narrow search finds no PRODUCT at all, the
 *    raw words are re-run and that answer is shown instead. The dropdown keeps
 *    answering somebody who is still typing, and the narrow result only wins
 *    when it has something to say.
 * 2. NO INDONESIAN COLOURS. The descriptions are already Bahasa ("Gamis panjang
 *    busui friendly dengan lengan kerut panjang bermanset. Bahan: Wollycrepe"),
 *    so garment words come through `body:` by themselves — gamis 92 of the 97
 *    found today, lengan 178, kancing 139, katun 62. Colours do not: the titles
 *    carry English colour names ("Black Taima") and the descriptions never
 *    mention colour, so `hitam` and `putih` both dropped to 0. COLOURS below is
 *    that bridge, and the point is that it is OURS — a short list that can be
 *    read and corrected, instead of Shopify deciding that Chandana means
 *    Kandara. The English words in it were taken from Aska's own 492 live
 *    product titles, so every one of them matches something.
 */
(() => {
  /** Bahasa colour -> the English colour words that appear in Aska's titles. */
  const COLOURS = {
    hitam: ['black', 'midnight'],
    putih: ['offwhite', 'white', 'ivory', 'eggshell', 'chalk', 'ecru', 'coconut', 'oatmilk', 'frost'],
    krem: ['cream', 'beige', 'oat', 'oatmeal', 'sand', 'latte', 'ecru'],
    coklat: ['coffee', 'espresso', 'cocoa', 'camel', 'taupe', 'walnut', 'truffle', 'wood', 'bark', 'milo', 'clay', 'mocha'],
    cokelat: ['coffee', 'espresso', 'cocoa', 'camel', 'taupe', 'walnut', 'truffle', 'wood', 'bark', 'milo', 'clay', 'mocha'],
    abu: ['grey', 'ash'],
    kelabu: ['grey', 'ash'],
    biru: ['blue', 'navy', 'denim', 'skyblue', 'lake', 'ocean', 'midnight'],
    merah: ['maroon', 'burgundy', 'brick', 'sangria', 'raspberry', 'red'],
    pink: ['pink', 'blush', 'rose', 'petal', 'candy', 'mauve'],
    hijau: ['sage', 'monstera', 'mint', 'green', 'olive'],
    kuning: ['mustard', 'yellow'],
    ungu: ['lilac', 'plum', 'mauve', 'lavender', 'purple'],
    emas: ['gold'],
    perak: ['silver'],
    nude: ['nude', 'beige', 'sand'],
  };

  /** Already carries a field qualifier — somebody typed their own query. */
  const QUALIFIED = /(^|\s)(title|body|variants\.sku):/;
  /** Characters that would break Shopify's query parser rather than narrow it. */
  const UNSAFE = /["'()\[\]{}:*~^]/g;

  const group = (word) => {
    const alts = COLOURS[word.toLowerCase()] || [];
    const parts = [];
    for (const w of [word, ...alts]) parts.push(`title:${w}`, `body:${w}`);
    // Last, and after an OR, so the displayed terms can be rebuilt in Liquid by
    // taking the first token of each group. See sections/main-search.liquid.
    parts.push(`variants.sku:${word}`);
    return parts.join(' OR ');
  };

  /** The query to send, or null to leave Shopify's own search alone. */
  const qualify = (raw) => {
    const text = String(raw || '').trim();
    if (!text || QUALIFIED.test(text)) return null;
    const words = text.split(/\s+/).map((w) => w.replace(UNSAFE, '')).filter(Boolean);
    if (!words.length || words.some((w) => w.includes(':'))) return null;
    return words.map(group).join(' ');
  };

  /** Did a predictive-search answer contain any PRODUCT? */
  const hasProducts = (body) => {
    const text = String(body || '');
    if (text.trimStart().startsWith('{')) {
      try {
        return (JSON.parse(text)?.resources?.results?.products || []).length > 0;
      } catch {
        return true; // unreadable: treat as an answer rather than retrying blind
      }
    }
    // The rendered section only prints this class when it has products.
    return text.includes('predictive-search__products');
  };

  // ---------------------------------------------------------------- dropdown
  //
  // Prestige's <predictive-search> builds the URL itself inside theme.js and
  // reads the input's value as it is typed, so there is nothing to hook but the
  // request. Wrapping fetch also means the theme keeps its own abort and cache
  // behaviour, and a Prestige update cannot silently drop our change.
  const original = window.fetch;
  window.fetch = function (input, init) {
    try {
      if (typeof input === 'string' && input.includes('/search/suggest')) {
        const url = new URL(input, location.origin);
        const raw = url.searchParams.get('q');
        const narrowed = qualify(raw);
        if (narrowed) {
          url.searchParams.set('q', narrowed);
          return original.call(window, url.toString(), init).then(async (res) => {
            if (!res.ok) return original.call(window, input, init);
            const body = await res.text();
            if (hasProducts(body)) {
              return new Response(body, { status: res.status, statusText: res.statusText, headers: res.headers });
            }
            // Nothing by name — answer with the raw words, which is what keeps
            // a half-typed "chandan" from showing an empty dropdown.
            return original.call(window, input, init);
          });
        }
      }
    } catch {
      // A search is never worth breaking the page for.
    }
    return original.call(window, input, init);
  };

  // ------------------------------------------------------------ results page
  //
  // THE URL IS NOT TOUCHED (Andre, 2026-10-01: "i prefer the url not to change
  // since i have used it in many places including ads"). So /search?q=gamis is
  // still answered by Shopify's own broad search, and the narrow one is fetched
  // over the Section Rendering API and swapped into the page. Nothing intercepts
  // the form: it submits exactly the words that were typed, as it always did,
  // and an ad link that has been live for months lands on the same URL.
  //
  // main-search.liquid holds the broad grid back until this finishes, and shows
  // it unchanged if anything here declines to answer — a search that is merely
  // too broad is a worse outcome than no search at all, but only slightly.
  const reveal = (el) => {
    el?.classList.remove('kano-search-pending');
    document.querySelectorAll('.kano-search-waiting').forEach((w) => w.remove());
  };

  /** Pagination inside the swapped-in results must link back to HIS url shape. */
  const keepOurUrl = (root) => {
    root.querySelectorAll('a[href*="page="]').forEach((link) => {
      try {
        const page = new URL(link.getAttribute('href'), location.origin).searchParams.get('page');
        const ours = new URL(location.href);
        if (page) ours.searchParams.set('page', page);
        else ours.searchParams.delete('page');
        link.setAttribute('href', ours.toString());
      } catch {
        // Leave the theme's own link alone rather than break it.
      }
    });
  };

  const narrowThePage = async () => {
    const broad = document.querySelector('[data-kano-search]');
    if (!broad) return; // no results at all, or not a search page
    const narrowed = qualify(broad.getAttribute('data-kano-terms'));
    if (!narrowed) return reveal(broad); // already narrow, or nothing we handle

    const url = new URL('/search', location.origin);
    url.searchParams.set('q', narrowed);
    url.searchParams.set('section_id', broad.getAttribute('data-kano-search'));
    const page = new URL(location.href).searchParams.get('page');
    if (page) url.searchParams.set('page', page);

    try {
      const res = await original.call(window, url.toString());
      if (!res.ok) return reveal(broad);
      const fresh = new DOMParser()
        .parseFromString(await res.text(), 'text/html')
        .querySelector('[data-kano-search]');
      // No section, or a section holding no product: nothing matched the name or
      // the description, so Shopify's broad answer is the better one to show.
      if (!fresh || !fresh.querySelectorAll('product-list > *').length) return reveal(broad);
      keepOurUrl(fresh);
      fresh.classList.remove('kano-search-pending');
      broad.replaceWith(fresh);
      document.querySelectorAll('.kano-search-waiting').forEach((w) => w.remove());
    } catch {
      reveal(broad);
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', narrowThePage);
  } else {
    narrowThePage();
  }
})();
